'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, Users, DollarSign, TrendingUp, TrendingDown, MoreHorizontal, Search, Music2, CheckCircle, Clock, XCircle, BarChart3, Zap, Bell } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Icon from '@/components/ui/AppIcon';


const kpis = [
  { label: 'Total Subscribers', value: '247', change: '+12', up: true, icon: Users, color: 'var(--neon-gold)' },
  { label: 'Active Platforms', value: '198', change: '+8', up: true, icon: CheckCircle, color: 'var(--neon-cyan)' },
  { label: 'Monthly Revenue', value: '$18,420', change: '+$1,240', up: true, icon: DollarSign, color: 'var(--neon-purple)' },
  { label: 'Churn Rate', value: '2.4%', change: '-0.3%', up: false, icon: TrendingDown, color: 'var(--neon-pink)' },
];

const revenueData = [
  { month: 'Jan', revenue: 11200 },
  { month: 'Feb', revenue: 12800 },
  { month: 'Mar', revenue: 13400 },
  { month: 'Apr', revenue: 14100 },
  { month: 'May', revenue: 15600 },
  { month: 'Jun', revenue: 16200 },
  { month: 'Jul', revenue: 17800 },
  { month: 'Aug', revenue: 18420 },
];

const subscribers = [
  { id: 1, name: 'Platinum Sound Events', owner: 'Marcus T.', plan: 'Professional', status: 'active', mrr: 99, joined: '2026-01-15', city: 'Atlanta, GA' },
  { id: 2, name: 'DJ Destiny Rose Entertainment', owner: 'Destiny R.', plan: 'Starter', status: 'active', mrr: 49, joined: '2026-02-03', city: 'Houston, TX' },
  { id: 3, name: 'NightLife Pro Events', owner: 'Kevin M.', plan: 'Enterprise', status: 'active', mrr: 199, joined: '2025-11-20', city: 'Chicago, IL' },
  { id: 4, name: 'Bass Drop Entertainment', owner: 'Tanya W.', plan: 'Professional', status: 'trial', mrr: 0, joined: '2026-08-28', city: 'Miami, FL' },
  { id: 5, name: 'Vibe Masters DJ Co.', owner: 'Darius L.', plan: 'Starter', status: 'active', mrr: 49, joined: '2026-03-10', city: 'Dallas, TX' },
  { id: 6, name: 'Elite Events Group', owner: 'Sandra K.', plan: 'Professional', status: 'suspended', mrr: 99, joined: '2026-04-22', city: 'Phoenix, AZ' },
  { id: 7, name: 'Rhythm & Soul Events', owner: 'Jerome B.', plan: 'Enterprise', status: 'active', mrr: 199, joined: '2025-09-14', city: 'New York, NY' },
  { id: 8, name: 'Party Starters LLC', owner: 'Alicia M.', plan: 'Starter', status: 'trial', mrr: 0, joined: '2026-07-30', city: 'Denver, CO' },
];

const recentSignups = [
  { name: 'Bass Drop Entertainment', plan: 'Professional', time: '2 hours ago' },
  { name: 'Party Starters LLC', plan: 'Starter', time: '1 day ago' },
  { name: 'Sound Wave Events', plan: 'Professional', time: '2 days ago' },
  { name: 'Groove Masters DJ', plan: 'Enterprise', time: '3 days ago' },
];

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  active: { label: 'Active', color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/30', icon: CheckCircle },
  trial: { label: 'Trial', color: 'text-accent', bg: 'bg-accent/10 border-accent/30', icon: Clock },
  suspended: { label: 'Suspended', color: 'text-red-400', bg: 'bg-red-400/10 border-red-400/30', icon: XCircle },
};

const PLAN_COLORS: Record<string, string> = {
  Starter: 'text-muted-foreground',
  Professional: 'text-primary',
  Enterprise: 'text-accent',
};

export default function WhiteLabelAdmin() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const filtered = subscribers.filter(s => {
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase()) || s.owner.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || s.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card/95 backdrop-blur-md border-b neon-border-gold">
        <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link href="/white-label" className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm">
              <ArrowLeft size={16} /> Back
            </Link>
            <div className="w-px h-5 bg-border" />
            <div className="flex items-center gap-2">
              <BarChart3 size={16} className="text-primary" />
              <span className="font-semibold text-sm">Platform Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg gold-gradient flex items-center justify-center">
              <Music2 size={13} className="text-black" />
            </div>
            <span className="font-bold text-sm gold-gradient-text">EventPro Suite</span>
          </div>
          <div className="flex items-center gap-3">
            <button className="p-2 rounded-lg border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-all">
              <Bell size={16} />
            </button>
            <button className="px-4 py-2 rounded-xl border border-primary/40 text-primary text-sm font-semibold hover:bg-primary/10 transition-all flex items-center gap-2">
              <Zap size={14} /> Send Announcement
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-6 py-10 space-y-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="card-glass rounded-2xl p-5 border border-border">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${kpi.color}15` }}>
                    <Icon size={18} style={{ color: kpi.color }} />
                  </div>
                  <div className={`flex items-center gap-1 text-xs font-semibold ${kpi.up ? 'text-green-400' : 'text-red-400'}`}>
                    {kpi.up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {kpi.change}
                  </div>
                </div>
                <p className="text-2xl font-extrabold text-foreground mb-1">{kpi.value}</p>
                <p className="text-xs text-muted-foreground">{kpi.label}</p>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Revenue Chart */}
          <div className="lg:col-span-2 card-glass rounded-2xl p-6 border border-border">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-bold text-lg">Monthly Revenue</h2>
              <span className="text-xs font-mono text-muted-foreground">2026 YTD</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={revenueData} barSize={28}>
                <XAxis dataKey="month" tick={{ fill: '#8888AA', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8888AA', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: '#12121A', border: '1px solid rgba(255,215,0,0.2)', borderRadius: 8, color: '#F0F0F5', fontSize: 12 }}
                  formatter={(v: number) => [`$${v.toLocaleString()}`, 'Revenue']}
                />
                <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                  {revenueData.map((entry, i) => (
                    <Cell key={i} fill={i === revenueData.length - 1 ? '#FFD700' : 'rgba(255,215,0,0.3)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Recent Signups */}
          <div className="card-glass rounded-2xl p-6 border border-border">
            <h2 className="font-bold text-lg mb-5">Recent Signups</h2>
            <div className="space-y-4">
              {recentSignups.map((s, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Music2 size={13} className="text-black" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{s.name}</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${PLAN_COLORS[s.plan]}`}>{s.plan}</span>
                      <span className="text-xs text-muted-foreground">· {s.time}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Subscriber Table */}
        <div className="card-glass rounded-2xl border border-border overflow-hidden">
          <div className="p-6 border-b border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <h2 className="font-bold text-lg">Subscriber Directory</h2>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-56">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search subscribers..."
                  className="w-full pl-9 pr-4 py-2 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary text-sm"
                />
              </div>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="px-3 py-2 rounded-lg bg-muted border border-border text-foreground text-sm focus:outline-none focus:border-primary"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {['Business', 'Owner', 'Plan', 'Status', 'MRR', 'Joined', 'Actions'].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((sub, i) => {
                  const status = STATUS_CONFIG[sub.status];
                  const StatusIcon = status.icon;
                  return (
                    <tr key={sub.id} className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg gold-gradient flex items-center justify-center flex-shrink-0">
                            <Music2 size={11} className="text-black" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{sub.name}</p>
                            <p className="text-xs text-muted-foreground">{sub.city}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-muted-foreground">{sub.owner}</td>
                      <td className="px-5 py-3">
                        <span className={`text-sm font-semibold ${PLAN_COLORS[sub.plan]}`}>{sub.plan}</span>
                      </td>
                      <td className="px-5 py-3">
                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${status.color} ${status.bg}`}>
                          <StatusIcon size={11} />
                          {status.label}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm font-mono text-foreground">
                        {sub.mrr > 0 ? `$${sub.mrr}` : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-5 py-3 text-xs text-muted-foreground font-mono">{sub.joined}</td>
                      <td className="px-5 py-3">
                        <button className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all">
                          <MoreHorizontal size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 border-t border-border flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Showing {filtered.length} of {subscribers.length} subscribers</p>
            <button className="text-xs text-primary hover:underline font-semibold">Export CSV</button>
          </div>
        </div>
      </div>
    </main>
  );
}
